import { filter } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography, TableContainer, useTheme, Button, alpha,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import TagsInput from '../components/TagsInput';
import MenuPopover from '../components/MenuPopover';

// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SnackBar from '../components/Snackbar';
import CustomPagination from '../components/CustomPagination';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import CreateButton from '../components/CreateButton';
import { APIGetAllCampaigns, APIDeleteCampaign, APIPublishCampaign } from '../utils/apiCalled';
import { usePrivilege } from '../PrivilegeContext';
import CampaignsMoreMenu from '../sections/@dashboard/user/CampaignsMoreMenu';
import MoreCampaign from '../components/dashboard/MoreCampaign';
import PageCampaign from '../components/dashboard/PageCampaign';
import { fDateTime } from '../utils/formatTime';
import Label from '../components/Label';

const TABLE_HEAD = [
  { id: 'campaignName', label: 'Campaign', alignRight: false },
  { id: 'lastTriggered', label: 'Last Triggered', alignRight: false },
  { id: 'scheduledAt', label: 'Scheduled Time', alignRight: false },
  { id: 'targetAudience', label: 'Target Audience', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

const tableStyle = {
  border: '0.063rem solid #EAECF0',
  borderTop: '0.094rem solid #EAECF0',
  borderBottom: '0.094rem solid #EAECF0',
  boxShadow: '0rem 0.063rem 0.188rem rgba(16, 24, 40, 0.1), 0rem 0.063rem 0.125rem rgba(16, 24, 40, 0.06)',
  borderRadius: '0.75rem',
};

const loadingStyle = {
  position: 'absolute',
  zIndex: 10,
  top: '35%',
  left: '55%',
  transform: 'translate(-50%, -50%)',
};

function descendingComparator(a, b, orderBy) {
  const aValue = a[orderBy];
  const bValue = b[orderBy];

  // Handle null/undefined values - put them at the end
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1; // null values go to the end
  if (bValue == null) return -1; // null values go to the end

  // Handle date fields (lastTriggered, scheduledAt, createdAt)
  if (orderBy === 'lastTriggered' || orderBy === 'scheduledAt' || orderBy === 'createdAt') {
    const aDate = aValue instanceof Date ? aValue : new Date(aValue);
    const bDate = bValue instanceof Date ? bValue : new Date(bValue);

    // Check if dates are valid
    if (Number.isNaN(aDate.getTime()) && Number.isNaN(bDate.getTime())) return 0;
    if (Number.isNaN(aDate.getTime())) return 1; // Invalid dates go to the end
    if (Number.isNaN(bDate.getTime())) return -1; // Invalid dates go to the end

    return bDate - aDate; // Descending: newer dates first
  }

  // Default comparison for other fields (strings, numbers)
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const filterStatus = ['On Demand', 'Published', 'Scheduled'];

function applySortFilter(array, comparator, query, statusFilter) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  let filtered = stabilizedThis.map((el) => el[0]);

  // Apply status filter
  if (statusFilter && statusFilter.length > 0) {
    filtered = filter(filtered, (_campaign) => statusFilter.includes(_campaign.status));
  }

  // Apply name filter if exists
  if (query) {
    filtered = filter(
      filtered,
      (_campaign) => _campaign.campaignName?.toLowerCase().indexOf(query.toLowerCase()) !== -1,
    );
  }

  return filtered;
}

// Helper function to format date and time
const formatDateTimeDisplay = (date) => {
  if (!date) return null;
  const dateObj = new Date(date);
  return {
    dateStr: format(dateObj, 'do MMMM yyyy'), // "12th December 2025"
    timeStr: format(dateObj, 'h:mm a'), // "11:19 AM"
  };
};

export default function Notifications() {
  const theme = useTheme();
  const { role } = usePrivilege();

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);
  const [order, setOrder] = useState('desc'); // Changed to 'desc' for newest first
  const [orderBy, setOrderBy] = useState('lastTriggered'); // Changed to sort by lastTriggered
  const [filterName, setFilterName] = useState('');
  const [openFilterPopUp, setOpenFilterPopUp] = useState(null);

  // Filter state
  const [getFilter, setGetFilter] = useState({
    status: null,
  });

  const [dupFilter, setDupFilter] = useState({
    status: [],
  });

  // Data state
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCampaigns, setTotalCampaigns] = useState(0);

  // UI state
  const [openPageCampaign, setOpenPageCampaign] = useState(false);
  const [openMoreCampaign, setOpenMoreCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  // Snackbar state
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState('');
  const [mainMsg, setMainMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [succMsg, setSuccMsg] = useState('');

  // Modal trigger state
  const [modalTriggerAPICalled, setModalTriggerAPICalled] = useState(false);

  // Helper function to show error snackbar
  const showError = (message = 'Error!, Please try again') => {
    setLoading(false);
    setOpen(true);
    setVariant('error');
    setMainMsg('Error Encountered');
    setErrMsg(message);
  };

  // Helper function to show success snackbar
  const showSuccess = (mainMessage, successMessage) => {
    setLoading(false);
    setOpen(true);
    setVariant('success');
    setMainMsg(mainMessage);
    setSuccMsg(successMessage);
    setErrMsg('');
  };

  // API Functions
  const fetchCampaigns = useCallback(async () => {
    try {
      const statusFilter = getFilter.status || null;
      const result = await APIGetAllCampaigns(setLoading, page + 1, rowsPerPage, statusFilter);
      if (result.status === 200) {
        const { data } = result.data;
        setCampaigns(data?.campaigns || []);
        // Store total count from API pagination
        if (data?.pagination?.total !== undefined) {
          setTotalCampaigns(data.pagination.total);
        }
        setLoading(false);
      } else {
        showError();
      }
    } catch (err) {
      showError();
    }
  }, [page, rowsPerPage, getFilter.status]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Table handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Campaign handlers
  const handleOpenPageCampaignCreate = () => {
    setSelectedCampaign(null); // No campaign selected = Create mode
    setOpenPageCampaign(true); // Open PageCampaign
    setOpenMoreCampaign(false); // Close MoreCampaign if open
  };

  const handleOpenMoreCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setOpenMoreCampaign(true);
    setOpenPageCampaign(false);
  };

  const handleCloseMoreCampaign = () => {
    setSelectedCampaign(null);
    setOpenMoreCampaign(false);
    setOpenPageCampaign(false);
    if (modalTriggerAPICalled) { // Checks OLD value before state updates!
      fetchCampaigns();
    }
  };

  const handleOpenPageCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setOpenMoreCampaign(false);
    setOpenPageCampaign(true);
  };

  // Delete handlers
  const handleOpenDeleteModal = () => setDeleteModal(true);
  const handleCloseDeleteModal = () => setDeleteModal(false);

  const handleDelete = async (campaign) => {
    try {
      const result = await APIDeleteCampaign(setLoading, campaign._id);
      if (result.status === 200) {
        setLoading(false);
        setDeleteModal(false);
        setOpen(true);
        setVariant('success');
        setMainMsg('Campaign Successfully Removed');
        setSuccMsg(`Campaign ${campaign.campaignName} has been successfully removed`);
        setModalTriggerAPICalled(true);
        setOpenMoreCampaign(false);
        fetchCampaigns();
      } else {
        setLoading(false);
        setDeleteModal(false);
        setOpen(true);
        setVariant('error');
        setMainMsg('Error Deleting Campaign');
        setErrMsg('Error deleting campaign!, Please try again');
      }
    } catch (err) {
      setLoading(false);
      setDeleteModal(false);
      setOpen(true);
      setVariant('error');
      setMainMsg('Error Deleting Campaign');
      showError('Error deleting campaign!, Please try again');
    }
  };

  // Publish handler
  const handleRunCampaign = async (campaign) => {
    try {
      const result = await APIPublishCampaign(setLoading, campaign._id, campaign.targetAudience);
      if (result.status === 200) {
        showSuccess('Campaign Successfully Published', `Campaign ${campaign.campaignName} has been successfully published`);
        setModalTriggerAPICalled(true);
        fetchCampaigns();
      } else {
        showError('Error publishing campaign!, Please try again');
      }
    } catch (err) {
      showError('Error publishing campaign!, Please try again');
    }
  };

  // Filter handlers
  const closePopup = () => {
    setOpenFilterPopUp(null);
  };

  const clearFilterButton = () => {
    setGetFilter({ status: null });
    setDupFilter({ status: [] });
    setPage(0);
  };

  const filterButton = () => {
    setPage(0);
    closePopup();
  };

  // Helper functions
  const getScheduledTime = (campaign) => {
    if (campaign.scheduledAt) {
      return fDateTime(campaign.scheduledAt);
    }
    if (campaign.alertTime?.scheduled) {
      return 'Scheduled (No specific time set)';
    }
    return '-';
  };

  const getTargetAudienceDisplay = (campaign) => {
    const COUNTRY_CODES = [
      { code: 'MY', name: 'Malaysia' },
      { code: 'OM', name: 'Oman' },
    ];

    const targetAudience = campaign?.targetAudience || 'Everyone';

    if (targetAudience === 'Everyone') {
      return 'Everyone';
    }

    // Check if it's a country code
    const countryCodeValues = COUNTRY_CODES.map((c) => c.code);
    if (countryCodeValues.includes(targetAudience)) {
      const country = COUNTRY_CODES.find((c) => c.code === targetAudience);
      return country ? country.name : targetAudience;
    }

    // Handle legacy data with selectedCountryCodes
    if (campaign?.selectedCountryCodes && campaign.selectedCountryCodes.length > 0) {
      const [firstCode] = campaign.selectedCountryCodes;
      const country = COUNTRY_CODES.find((c) => c.code === firstCode);
      return country ? country.name : firstCode;
    }

    return targetAudience;
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'On Demand':
        return 'info';
      case 'Scheduled':
        return 'warning';
      case 'Published':
        return 'success';
      case 'Draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const statusFilterArray = dupFilter.status || [];
  const filteredCampaigns = applySortFilter(
    campaigns,
    getComparator(order, orderBy),
    filterName,
    statusFilterArray,
  );
  const paginatedCampaigns = filteredCampaigns;
  const emptyRows = Math.max(0, rowsPerPage - paginatedCampaigns.length);

  const paginationTotal = totalCampaigns;

  return (
    <Page title="Notification Campaigns">
      <Container maxWidth="xl" sx={{ mt: (!openMoreCampaign && !openPageCampaign) ? 12 : 0 }}>
        <SnackBar
          open={open}
          alert={variant}
          handleClose={() => setOpen(false)}
          errorMsg={errMsg}
          successMsg={succMsg}
          mainMsg={mainMsg}
        />

        {(!openMoreCampaign && !openPageCampaign) && (
          <>
            <Box sx={
              {
                pb: 5, display: 'flex', flexDirection: 'column', alignItems: 'left',
              }
            }
            >
              <Typography variant="h3">Notification Campaigns</Typography>
              <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>
                Manage the campaigns which are sent out to users
              </Typography>
            </Box>

            {loading && <CircularProgress color="success" sx={loadingStyle} />}

            <Box sx={tableStyle}>
              <Box sx={{
                mr: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
              >
                <UserListToolbar
                  numSelected={0}
                  filterName={filterName}
                  onFilterName={handleFilterByName}
                  popupfilter={setOpenFilterPopUp}
                  labelName="Search Campaign Name"
                  displayFilter
                />
                <CreateButton type="Campaign" handleOpen={handleOpenPageCampaignCreate} />
              </Box>

              <MenuPopover
                open={
                Boolean(openFilterPopUp)
              }
                anchorEl={openFilterPopUp}
                onClose={closePopup}
                styleWidth={500}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 1 }}>Filter By</Typography>
                  <TagsInput
                    fullWidth
                    variant="outlined"
                    id="status"
                    name="status"
                    placeholder="add tags"
                    label="Status"
                    type="autocomplete"
                    selectList={filterStatus}
                    dupFilter={dupFilter}
                    setDupFilter={setDupFilter}
                    keyName="status"
                    setGetFilter={setGetFilter}
                    getFilter={getFilter}
                    sx={{ marginBottom: 1.5 }}
                  />
                </Box>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 2,
                  marginBottom: 2,
                }}
                >
                  <Button
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.300',
                      px: 2,
                      pt: 1,
                      pb: 1,
                      width: 130,
                      marginRight: '8px',
                      color: theme.palette.grey[700],
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.grey[500], 0.08),
                      },
                    }}
                    component={RouterLink}
                    to="#"
                    onClick={clearFilterButton}
                  >
                    <Typography sx={{ fontWeight: 'fontWeightMedium' }}>Clear Filter</Typography>
                  </Button>
                  <Button
                    sx={{
                      border: 'none',
                      px: 2,
                      pt: 1,
                      pb: 1,
                      width: 130,
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.90),
                      },
                    }}
                    component={RouterLink}
                    to="#"
                    onClick={filterButton}
                  >
                    <Typography sx={{ fontWeight: 'fontWeightMedium' }}>Filter</Typography>
                  </Button>
                </Box>
              </MenuPopover>

              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={campaigns.length}
                      numSelected={0}
                      onRequestSort={handleRequestSort}
                      onSelectAllClick={() => {}}
                    />
                    <TableBody>
                      {paginatedCampaigns.map((row) => {
                        const {
                          _id,
                          campaignName,
                          lastTriggered,
                          status,
                        } = row;

                        return (
                          <TableRow hover key={_id} tabIndex={-1}>
                            <TableCell align="left" component="th" scope="row">
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Typography variant="secondary" sx={{ color: theme.palette.grey[700] }}>
                                  {campaignName || '-'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="left">
                              {lastTriggered ? (() => {
                                const formatted = formatDateTimeDisplay(lastTriggered);
                                return formatted ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="secondary" sx={{ color: theme.palette.grey[700] }}>
                                      {formatted.dateStr}
                                    </Typography>
                                    <Typography variant="secondary" sx={{ color: theme.palette.grey[700], fontSize: '0.70rem' }}>
                                      {formatted.timeStr}
                                    </Typography>
                                  </Box>
                                ) : '-';
                              })() : (
                                <Typography variant="secondary" sx={{ color: theme.palette.grey[700] }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant="secondary" sx={{ color: theme.palette.grey[700] }}>
                                {getScheduledTime(row)}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant="secondary" sx={{ color: theme.palette.grey[700] }}>
                                {getTargetAudienceDisplay(row)}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              {status ? (
                                <Label variant="ghost" sx={{ fontWeight: 'fontWeightMedium', fontSize: '0.80rem' }} color={getStatusColor(status)}>
                                  {status}
                                </Label>
                              ) : (
                                <Typography variant="secondary" sx={{ color: theme.palette.grey[700] }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <CampaignsMoreMenu
                                campaign={row}
                                handleSeeMore={handleOpenMoreCampaign}
                                handleEditOpen={handleOpenPageCampaign}
                                handleDeleteOpen={handleOpenDeleteModal}
                                handleClose={handleCloseDeleteModal}
                                deleteModal={deleteModal}
                                handleDelete={handleDelete}
                                handleRunCampaign={handleRunCampaign}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <CustomPagination
                page={page}
                totalNum={paginationTotal}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
              />
            </Box>
          </>
        )}
        {openMoreCampaign && (
          <MoreCampaign
            handleCloseMoreCampaigns={handleCloseMoreCampaign}
            campaign={selectedCampaign}
            handleDelete={handleDelete}
            deleteModal={deleteModal}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleCloseDeleteModal={handleCloseDeleteModal}
            handleOpenPageCampaigns={handleOpenPageCampaign}
            role={role}
          />
        )}

        {openPageCampaign && (
          <PageCampaign
            handleCloseMoreCampaigns={handleCloseMoreCampaign}
            campaign={selectedCampaign}
            setOpen={setOpen}
            setSuccMsg={setSuccMsg}
            setErrMsg={setErrMsg}
            setVariant={setVariant}
            setMainMsg={setMainMsg}
            setModalTriggerAPICalled={setModalTriggerAPICalled}
            fetchCampaigns={fetchCampaigns}
            role={role}
          />
        )}
      </Container>
    </Page>
  );
}
